import Slideshow from '../Slideshow';
import { useDarkMode } from '../DarkModeContext';
import useIsVisible from '../isVisible';
import { useRef } from 'react';

import '../../node_modules/video-react/dist/video-react.css';

const DieIcon = () => (
  <svg viewBox="0 0 100 110" className="w-full h-full drop-shadow-2xl" aria-hidden="true">
    {/* Pentagon silhouette of a D20 */}
    <polygon
      points="50,4 96,36 80,92 20,92 4,36"
      fill="rgba(255,255,255,0.07)"
      stroke="rgba(255,255,255,0.85)"
      strokeWidth="1.5"
    />
    {/* Facet lines */}
    <line x1="50" y1="4"  x2="4"  y2="36" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
    <line x1="50" y1="4"  x2="96" y2="36" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
    <line x1="4"  y1="36" x2="96" y2="36" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
    <line x1="4"  y1="36" x2="50" y2="92" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
    <line x1="96" y1="36" x2="50" y2="92" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
    <line x1="4"  y1="36" x2="20" y2="92" stroke="rgba(255,255,255,0.2)"  strokeWidth="1"/>
    <line x1="96" y1="36" x2="80" y2="92" stroke="rgba(255,255,255,0.2)"  strokeWidth="1"/>
    {/* Number */}
    <text x="50" y="68" textAnchor="middle" fill="white" fontSize="24" fontWeight="700" fontFamily="sans-serif">20</text>
  </svg>
);

const Badge = ({ text }) => (
  <span className="inline-block px-3 py-1 text-xs rounded-full bg-white/15 text-white border border-white/30 mr-2 mb-2">
    {text}
  </span>
);

const FeatureCard = ({ icon, title, desc, isDarkMode }) => (
  <div className={`rounded-2xl p-6 border flex flex-col gap-3 ${
    isDarkMode
      ? 'bg-gray-800/60 border-gray-700'
      : 'bg-white border-Template-2 shadow-sm'
  }`}>
    <div className="text-3xl">{icon}</div>
    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-Template-0'}`}>{title}</h3>
    <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{desc}</p>
  </div>
);

function Homepage() {
  const { isDarkMode } = useDarkMode();

  const featureRef = useRef();
  const slideshowRef = useRef();
  const featureVisible = useIsVisible(featureRef);
  const slideshowVisible = useIsVisible(slideshowRef);

  const features = [
    {
      icon: '🎲',
      title: 'Intelligente Erkennung',
      desc: 'Der LSM6DSV 9-Achsen-IMU erkennt per SFLP-Sensorfusion, welche Seite oben liegt, mit 20 kalibrierten Flächennormalen und einem Lookup-Table.',
    },
    {
      icon: '💡',
      title: 'LED-Anzeige',
      desc: '1×1 mm adressierbare RGB-LEDs auf einer Flex-PCB beleuchten die gewürfelte Zahl direkt auf dem Würfel, programmierbar per App.',
    },
    {
      icon: '⚡',
      title: 'Kabelloses Laden',
      desc: 'QI-Ladestation – einfach USB-C einstecken, Würfel einlegen, fertig. 160 mAh LiPo lädt in 2 Stunden und hält 4 Stunden im Betrieb.',
    },
    {
      icon: '📱',
      title: 'App-Steuerung',
      desc: 'Via Bluetooth Low Energy mit der SmartDice-App verbinden. Farben, Würfeltyp und Spielmodi direkt am Smartphone konfigurieren.',
    },
  ];

  return (
    <div>
      {/* ── Hero ── */}
      <section className="min-h-[580px] bg-gradient-to-br from-Template-0 via-Template-1 to-[#9b7dd4] flex items-center">
        <div className="max-w-6xl mx-auto px-8 py-16 w-full flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 text-white">
            <div className="flex flex-wrap mb-5">
              <Badge text="ESP32-C3" />
              <Badge text="Bluetooth LE" />
              <Badge text="QI Charging" />
              <Badge text="Flex PCB" />
              <Badge text="LSM6DSV-Gyro" />
            </div>
            <h1 className="text-7xl font-bold mb-4 leading-none tracking-tight">SmartDice</h1>
            <p className="text-xl opacity-90 mb-5 font-medium">
              Der intelligente D20-Würfel mit LED-Anzeige, kabelloser Ladestation und Smartphone-App.
            </p>
            <p className="text-base opacity-70 leading-relaxed max-w-lg">
              Ein universitäres Projekt, das physisches Spielerlebnis mit moderner Embedded-Technik verbindet –
              vom Flex-PCB-Design über ESP32-Firmware bis zur App.
            </p>
          </div>
          <div className="w-44 h-44 md:w-60 md:h-60 flex-shrink-0 animate-float">
            <DieIcon />
          </div>
        </div>
      </section>

      {/* ── Feature Cards ── */}
      <section
        ref={featureRef}
        className={`py-16 px-8 transition-opacity duration-700 ${featureVisible ? 'opacity-100' : 'opacity-0'} ${
          isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className={`text-3xl font-bold mb-1 text-center ${isDarkMode ? 'text-white' : 'text-Template-0'}`}>
            Features
          </h2>
          <p className={`text-center text-sm mb-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Was den SmartDice besonders macht
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f) => (
              <FeatureCard key={f.title} {...f} isDarkMode={isDarkMode} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Slideshow ── */}
      <section
        ref={slideshowRef}
        className={`py-12 transition-opacity duration-700 ${slideshowVisible ? 'opacity-100' : 'opacity-0'} ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        <div className="max-w-2xl mx-auto px-8">
          <h2 className={`text-3xl font-bold mb-8 text-center ${isDarkMode ? 'text-white' : 'text-Template-0'}`}>
            Einblicke
          </h2>
          <Slideshow />
        </div>
      </section>
    </div>
  );
}

export default Homepage;
